import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import Labeled from '../misc/Labeled';
import Button from '../buttons/Button';
import {
  CreateProductDto,
  ProductDto,
  UpdateProductDto,
} from '../../../services/model/marketplace.model';
import CurrencyInput from '../forms/CurrencyInput';

interface ProductModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  mode: 'CREATE' | 'EDIT';
  product?: ProductDto;
  onSubmit: (data: CreateProductDto | UpdateProductDto) => Promise<void>;
}

export default function ProductModal({
  open,
  setOpen,
  mode,
  product,
  onSubmit,
}: ProductModalProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.marketplaceProducts' });

  const [formData, setFormData] = useState<CreateProductDto>({
    name: '',
    description: '',
    price: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (mode === 'EDIT' && product) {
        setFormData({
          name: product.name,
          description: product.description || '',
          price: product.price,
        });
      } else {
        setFormData({
          name: '',
          description: '',
          price: 0,
        });
      }
    }
  }, [open, mode, product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Clean up the form data to only include defined values
      const cleanFormData = {
        name: formData.name.trim(),
        price: formData.price,
        ...(formData.description &&
          formData.description.trim() && { description: formData.description.trim() }),
      };

      if(!cleanFormData.name || !cleanFormData.description || !cleanFormData.price || cleanFormData.price <= 0) {
        toast.error(t('messages.invalidForm'));
        setIsSubmitting(false);
        return;
      }

      await onSubmit(cleanFormData);
      setOpen(false);
      toast.success(mode === 'CREATE' ? t('messages.created') : t('messages.updated'));
    } catch (error: any) {
      console.error('Error submitting product:', error);

      // Show specific error message if available
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(t('messages.failedToSave'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 w-full max-w-md rounded-[20px] bg-secondary-50 p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-primary-900">
          {mode === 'CREATE' ? t('createProduct') : t('updateProduct')}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Labeled label={t('name')}>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-300 focus:outline-none"
            placeholder={t('placeholders.name')}
            required
          />
        </Labeled>

        <Labeled label={t('description')}>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-300 focus:outline-none"
            placeholder={t('placeholders.description')}
            rows={3}
          />
        </Labeled>

        <Labeled label={t('price')}>
          <CurrencyInput
            type="text"
            className="w-full"
            handleChange={(value) => {
              setFormData({ ...formData, price: Math.round(value * 100) });
            }}
            value={formData.price ? `€${(formData.price / 100).toFixed(2)}` : undefined}
          />
        </Labeled>

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            {t('buttons.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || !formData.name || formData.price <= 0 || !formData.description?.trim()}>
            {isSubmitting
              ? t('buttons.saving')
              : mode === 'CREATE'
                ? t('buttons.create')
                : t('buttons.update')}
          </Button>
        </div>
      </form>
    </ModalLayout>
  );
}
